import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import Sidebar from "../components/Sidebar";
import { motion } from "framer-motion";
import { 
  Bot, 
  User, 
  CreditCard, 
  Save,
  Sparkles,
  MessageSquare,
  CheckCircle,
  Zap,
  Link2,
  ExternalLink,
  Crown,
  Users,
  TrendingUp,
  Bell,
  Shield,
  Smartphone,
  RefreshCw
} from "lucide-react";

const Settings = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("ai");
  
  const [aiStyle, setAiStyle] = useState("balanceado");
  const [trainingExamples, setTrainingExamples] = useState("");
  const [previewMessage, setPreviewMessage] = useState("");

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/nutritionist/profile`, { withCredentials: true });
      setProfile(response.data);
      setAiStyle(response.data.ai_style || "balanceado");
      setTrainingExamples(response.data.ai_training_examples?.join('\n') || "");
      generatePreview(response.data.ai_style || "balanceado");
    } catch (error) {
      console.error("Error fetching profile:", error);
      // Use default values
      setProfile({
        ai_style: "balanceado",
        ai_training_examples: [],
        bio: "",
        specialty: ""
      });
      generatePreview("balanceado");
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = (style) => {
    const previews = {
      formal: "Estimado cliente, le felicito por su excelente desempeño hoy. Su dedicación al plan nutricional es admirable. Continúe con este compromiso.",
      balanceado: "¡Excelente trabajo hoy! Me alegra ver tu compromiso con el plan. Tu constancia está dando resultados. ¡Sigue así!",
      casual: "¡Wow, lo hiciste genial hoy! 🎉 Se nota que le estás echando ganas. Así se hace, ¡vamos por más! 💪"
    };
    setPreviewMessage(previews[style] || previews.balanceado);
  };

  const handleStyleChange = (style) => {
    setAiStyle(style);
    generatePreview(style);
  };

  const handleSaveAI = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/nutritionist/ai-training`,
        {
          ai_style: aiStyle,
          ai_training_examples: trainingExamples.split('\n').filter(line => line.trim())
        },
        { withCredentials: true }
      );
      toast.success("¡Configuración de IA guardada!");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const styleOptions = [
    {
      value: "formal",
      label: "Formal",
      description: "Profesional y respetuoso. Ideal para clínicas.",
      icon: "👔"
    },
    {
      value: "balanceado",
      label: "Balanceado",
      description: "Profesional pero cercano. El más popular.",
      icon: "⚖️"
    },
    {
      value: "casual",
      label: "Cercano",
      description: "Amigable y motivador. Como un amigo.",
      icon: "🤗"
    }
  ];

  const integrations = [
    {
      name: "Fitia",
      description: "Sincroniza actividad física y ajusta metas automáticamente",
      icon: "🍎",
      color: "from-purple-500 to-pink-500",
      status: "coming_soon"
    },
    {
      name: "WhatsApp Business",
      description: "Conecta tu número de WhatsApp Business para mensajes automáticos",
      icon: "💬",
      color: "from-green-500 to-emerald-500",
      status: "active"
    },
    {
      name: "Google Calendar",
      description: "Sincroniza citas y recordatorios con tus clientes",
      icon: "📅",
      color: "from-blue-500 to-cyan-500",
      status: "coming_soon"
    },
    {
      name: "Stripe",
      description: "Acepta pagos y gestiona suscripciones",
      icon: "💳",
      color: "from-violet-500 to-purple-500",
      status: "coming_soon"
    }
  ];

  const plans = [
    {
      name: "Starter",
      price: 29,
      clients: 10,
      features: ["10 clientes", "500 interacciones/mes", "Soporte por email"],
      popular: false
    },
    {
      name: "Professional",
      price: 59,
      clients: 30,
      features: ["30 clientes", "1,500 interacciones/mes", "Soporte prioritario", "Análisis avanzado"],
      popular: true
    },
    {
      name: "Scale",
      price: 99,
      clients: 60,
      features: ["60 clientes", "Interacciones ilimitadas", "Soporte 24/7", "API access"],
      popular: false
    }
  ];

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#FAFAFA]">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64">
          <div className="max-w-4xl mx-auto">
            <div className="h-8 w-48 skeleton rounded-lg mb-6"></div>
            <div className="h-96 skeleton rounded-xl"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#FAFAFA]">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0F4D40]">Configuración</h1>
            <p className="text-gray-500 mt-1">Personaliza tu experiencia en HabitIA</p>
          </motion.div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 bg-white border border-gray-100 rounded-xl p-1 shadow-sm">
              <TabsTrigger value="ai" className="rounded-lg data-[state=active]:bg-[#0F4D40] data-[state=active]:text-white">
                <Bot className="w-4 h-4 mr-2" />
                Mi IA
              </TabsTrigger>
              <TabsTrigger value="integrations" className="rounded-lg data-[state=active]:bg-[#0F4D40] data-[state=active]:text-white">
                <Link2 className="w-4 h-4 mr-2" />
                Integraciones
              </TabsTrigger>
              <TabsTrigger value="account" className="rounded-lg data-[state=active]:bg-[#0F4D40] data-[state=active]:text-white">
                <User className="w-4 h-4 mr-2" />
                Mi Cuenta
              </TabsTrigger>
              <TabsTrigger value="plan" className="rounded-lg data-[state=active]:bg-[#0F4D40] data-[state=active]:text-white">
                <CreditCard className="w-4 h-4 mr-2" />
                Plan
              </TabsTrigger>
            </TabsList>

            {/* AI Tab */}
            <TabsContent value="ai" className="mt-4 space-y-6">
              {/* Style Selection */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6"
              >
                <h3 className="font-semibold text-[#0F4D40] mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Estilo de comunicación
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {styleOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleStyleChange(option.value)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        aiStyle === option.value
                          ? 'border-[#178A6B] bg-[#178A6B]/5'
                          : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-2xl">{option.icon}</span>
                        {aiStyle === option.value && (
                          <CheckCircle className="w-5 h-5 text-[#178A6B]" />
                        )}
                      </div>
                      <h4 className="font-semibold text-[#0F4D40]">{option.label}</h4>
                      <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Training Examples */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6"
              >
                <h3 className="font-semibold text-[#0F4D40] mb-2 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Entrena tu IA
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Pega ejemplos de mensajes que normalmente envías a tus clientes. La IA aprenderá tu estilo único.
                </p>
                
                <Textarea
                  value={trainingExamples}
                  onChange={(e) => setTrainingExamples(e.target.value)}
                  placeholder={"¡Hola! ¿Cómo te fue hoy con tu alimentación?\n\nRecuerda que cada pequeño paso cuenta.\n\n¡Excelente! Me encanta ver tu compromiso."}
                  rows={6}
                  className="font-mono text-sm rounded-xl border-gray-200"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Escribe un mensaje por línea. Mínimo 3-5 ejemplos para mejores resultados.
                </p>
              </motion.div>

              {/* Preview */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-r from-[#0F4D40]/5 to-[#178A6B]/5 border border-[#178A6B]/20 rounded-2xl p-6"
              >
                <h3 className="font-semibold text-[#0F4D40] mb-4 flex items-center gap-2">
                  <Bot className="w-5 h-5" />
                  Así suena tu IA ahora
                </h3>
                
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#0F4D40] rounded-full flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-[#178A6B] font-medium mb-1">HabitIA • Tu asistente</p>
                      <p className="text-gray-700">{previewMessage}</p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSaveAI}
                  disabled={saving}
                  className="mt-4 w-full sm:w-auto rounded-xl bg-[#178A6B] hover:bg-[#0F4D40]"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar configuración de IA
                    </>
                  )}
                </Button>
              </motion.div>
            </TabsContent>

            {/* Integrations Tab */}
            <TabsContent value="integrations" className="mt-4 space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-gray-500 mb-6">
                  Conecta HabitIA con otras herramientas para potenciar tu práctica.
                </p>

                <div className="grid gap-4">
                  {integrations.map((integration, index) => (
                    <motion.div
                      key={integration.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 bg-gradient-to-br ${integration.color} rounded-xl flex items-center justify-center shadow-lg`}>
                            <span className="text-2xl">{integration.icon}</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#0F4D40]">{integration.name}</h3>
                            <p className="text-sm text-gray-500">{integration.description}</p>
                          </div>
                        </div>
                        
                        {integration.status === "active" ? (
                          <div className="flex items-center gap-2">
                            <span className="flex items-center gap-1 text-sm text-[#178A6B] bg-[#178A6B]/10 px-3 py-1.5 rounded-full">
                              <CheckCircle className="w-4 h-4" />
                              Conectado
                            </span>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            className="rounded-xl border-gray-200"
                            disabled
                          >
                            <Zap className="w-4 h-4 mr-2" />
                            Próximamente
                          </Button>
                        )}
                      </div>

                      {integration.name === "Fitia" && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="bg-purple-50 rounded-xl p-4">
                            <h4 className="font-medium text-purple-700 mb-2">¿Qué podrás hacer con Fitia?</h4>
                            <ul className="text-sm text-purple-600 space-y-1">
                              <li>• Sincronizar pasos y actividad física diaria</li>
                              <li>• Ajustar metas calóricas según actividad</li>
                              <li>• Ver métricas de salud de tus clientes</li>
                              <li>• Notificaciones automáticas de logros</li>
                            </ul>
                          </div>
                        </div>
                      )}

                      {integration.name === "WhatsApp Business" && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
                            <div>
                              <p className="text-sm text-gray-600">Número conectado</p>
                              <p className="font-medium text-[#0F4D40]">+51 999 888 777</p>
                            </div>
                            <Button variant="outline" size="sm" className="rounded-lg">
                              Cambiar
                            </Button>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </TabsContent>

            {/* Account Tab */}
            <TabsContent value="account" className="mt-4 space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6"
              >
                <h3 className="font-semibold text-[#0F4D40] mb-6">Información personal</h3>
                
                <div className="flex items-center gap-4 mb-6">
                  {user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name}
                      className="w-20 h-20 rounded-full object-cover border-4 border-[#178A6B]/20"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-[#0F4D40] rounded-full flex items-center justify-center">
                      <span className="text-3xl text-white font-bold">
                        {user?.name?.charAt(0) || "U"}
                      </span>
                    </div>
                  )}
                  <div>
                    <h4 className="text-xl font-bold text-[#0F4D40]">{user?.name}</h4>
                    <p className="text-gray-500">{user?.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Nombre completo</Label>
                    <Input
                      value={user?.name || ""}
                      disabled
                      className="mt-1 bg-gray-50"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-500">Email</Label>
                    <Input
                      value={user?.email || ""}
                      disabled
                      className="mt-1 bg-gray-50"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-gray-500">Bio</Label>
                    <Textarea
                      placeholder="Cuéntanos sobre tu especialidad y experiencia..."
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-gray-500">Especialidad</Label>
                    <Input
                      placeholder="Ej: Nutrición deportiva, Pérdida de peso..."
                      className="mt-1"
                    />
                  </div>
                </div>

                <Button className="mt-6 rounded-xl bg-[#178A6B] hover:bg-[#0F4D40]">
                  <Save className="w-4 h-4 mr-2" />
                  Guardar cambios
                </Button>
              </motion.div>

              {/* Notifications Settings */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6"
              >
                <h3 className="font-semibold text-[#0F4D40] mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Notificaciones
                </h3>
                
                <div className="space-y-4">
                  {[
                    { label: "Clientes en riesgo", description: "Recibe alertas cuando un cliente lleva 2+ días sin check-in" },
                    { label: "Nuevos check-ins", description: "Notificación cuando un cliente hace su check-in diario" },
                    { label: "Milestones", description: "Celebra cuando tus clientes alcanzan rachas importantes" },
                    { label: "Resumen semanal", description: "Email con el resumen de adherencia de tus clientes" }
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div>
                        <p className="font-medium text-gray-700">{item.label}</p>
                        <p className="text-sm text-gray-500">{item.description}</p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                  ))}
                </div>
              </motion.div>
            </TabsContent>

            {/* Plan Tab */}
            <TabsContent value="plan" className="mt-4 space-y-6">
              {/* Current Plan */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-[#0F4D40] to-[#178A6B] rounded-2xl p-6 text-white"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-5 h-5 text-[#E57A4F]" />
                      <span className="text-sm text-white/80">Plan actual</span>
                    </div>
                    <h2 className="text-2xl font-bold">Professional</h2>
                    <p className="text-white/70 mt-1">$59/mes • Facturación mensual</p>
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-bold">12</p>
                    <p className="text-white/70">de 30 clientes</p>
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t border-white/20">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-white/70">Interacciones usadas</span>
                    <span>847 / 1,500</span>
                  </div>
                  <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white w-[56%] rounded-full"></div>
                  </div>
                </div>
              </motion.div>

              {/* Plans Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {plans.map((plan, index) => (
                  <motion.div
                    key={plan.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`bg-white border-2 rounded-2xl p-6 relative ${
                      plan.popular 
                        ? 'border-[#178A6B] shadow-lg shadow-[#178A6B]/10' 
                        : 'border-gray-100'
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-[#178A6B] text-white text-xs font-medium px-3 py-1 rounded-full">
                          Más popular
                        </span>
                      </div>
                    )}
                    
                    <h3 className="font-bold text-[#0F4D40] text-lg">{plan.name}</h3>
                    <div className="mt-2 mb-4">
                      <span className="text-3xl font-bold text-[#0F4D40]">${plan.price}</span>
                      <span className="text-gray-500">/mes</span>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-[#178A6B]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <Button
                      variant={plan.popular ? "default" : "outline"}
                      className={`w-full rounded-xl ${
                        plan.popular 
                          ? 'bg-[#178A6B] hover:bg-[#0F4D40]' 
                          : ''
                      }`}
                      disabled={plan.name === "Professional"}
                    >
                      {plan.name === "Professional" ? "Plan actual" : "Cambiar plan"}
                    </Button>
                  </motion.div>
                ))}
              </div>

              {/* Usage Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6"
              >
                <h3 className="font-semibold text-[#0F4D40] mb-4">Uso este mes</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-[#178A6B]" />
                      <span className="text-sm text-gray-500">Clientes activos</span>
                    </div>
                    <p className="text-2xl font-bold text-[#0F4D40]">12 / 30</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-5 h-5 text-[#E57A4F]" />
                      <span className="text-sm text-gray-500">Interacciones</span>
                    </div>
                    <p className="text-2xl font-bold text-[#0F4D40]">847 / 1,500</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-blue-500" />
                      <span className="text-sm text-gray-500">Adherencia promedio</span>
                    </div>
                    <p className="text-2xl font-bold text-[#0F4D40]">87%</p>
                  </div>
                </div>
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default Settings;
